import React, { useMemo, useState, useEffect, useCallback } from "react";
import Customtable from "../helper/Customtable";
import Swal from "sweetalert2";
import { post } from "../helper/api_helper";
import JobDocumentModal from "../components/JobDocumentModal";
import JobContainersModal from "../components/JobContainersModal";
import JobFormModal from "../components/JobFormModal";

const STATUS_LABELS = {
  1: "Yet to Arrive",
  2: "BE Filed",
  3: "Arrived",
  4: "Check Duty Payment",
  5: "Examination",
  6: "OOC",
  7: "Ready to Despatch",
  8: "Lot Completed",
};

// Same sections/order/labels as the All Jobs edit form (All_jobs.js
// `allFormFields`), but read-only and with no live master-data options —
// fields normally backed by a fetched select (consignee, shipper, liner,
// indenting agent, CFS, currency, license, bond, commodity) just show the
// resolved name/value the list API already sends instead of a dropdown.
const VIEW_SECTIONS = [
  { type: "section", label: "Job Information" },
  { name: "job_date", label: "Job Date", type: "date", col: 3 },
  {
    name: "job_type", label: "Job Type", type: "select", col: 3,
    options: [
      { value: "", label: "-select-" },
      { value: "import", label: "Import" },
      { value: "export", label: "Export" },
      { value: "transship", label: "Transship" },
    ],
  },
  {
    name: "status", label: "Status", type: "select", col: 3,
    options: [
      { value: 0, label: "-select-" },
      { value: 1, label: "Yet to Arrive" },
      { value: 2, label: "BE Filed" },
      { value: 3, label: "Arrived" },
      { value: 4, label: "Check Duty Payment" },
      { value: 5, label: "Examination" },
      { value: 6, label: "OOC" },
      { value: 7, label: "Ready to Despatch" },
      { value: 8, label: "Lot Completed" },
    ],
  },
  { name: "lot_no", label: "Lot No.", type: "text", col: 3 },

  { type: "section", label: "Party Details" },
  { name: "consignees_name", label: "Consignee", type: "text", col: 3 },
  { name: "shipper_name", label: "Shipper", type: "text", col: 3 },
  { name: "liner_name", label: "Liner / Shipping Line", type: "text", col: 3 },
  { name: "indenting_agent_name", label: "Indenting Agent Name", type: "text", col: 3 },

  { type: "section", label: "Bill of Lading" },
  { name: "bl_number", label: "BL Number", type: "text", col: 3 },
  {
    name: "bl_type", label: "BL Type", type: "select", col: 3,
    options: [
      { value: "original", label: "Original" },
      { value: "telex_release", label: "Telex Release" },
      { value: "sea_waybill", label: "Sea Waybill" },
      { value: "express_bl", label: "Express BL" },
    ],
  },
  { name: "bl_date", label: "BL Date", type: "date", col: 3 },
  { name: "invoice_no", label: "Invoice No.", type: "text", col: 3 },
  { name: "invoice_date", label: "Invoice Date", type: "date", col: 3 },
  { name: "invoice_value", label: "Invoice Value", type: "text", col: 3 },
  { name: "currency_display", label: "Currency", type: "text", col: 3 },
  { name: "hss", label: "High Sea Sale", type: "checkbox", col: 3 },
  { name: "hss_party", label: "Hss Party Name", type: "text", col: 3 },
  { name: "hss_invoice_no", label: "HSS Invoice No", type: "text", col: 3 },
  { name: "hss_invoice_date", label: "HSS Invoice Date", type: "date", col: 3 },

  { type: "section", label: "Vessel & Ports" },
  { name: "vessel_name", label: "Vessel Name", type: "text", col: 3 },
  { name: "voyage_no", label: "Voyage No.", type: "text", col: 3 },
  { name: "pol", label: "Port of Loading", type: "text", col: 3 },
  { name: "pod", label: "Port of Discharge", type: "text", col: 3 },

  { type: "section", label: "Arrival & Free Days" },
  { name: "ts_arrival", label: "T/S Arrival Date", type: "date", col: 3 },
  { name: "pod_arrival", label: "POD Arrival Date", type: "date", col: 3 },
  { name: "cfs_name", label: "CFS", type: "text", col: 3 },
  { name: "cfs_nomination", label: "CFS Nomination Date", type: "date", col: 3 },
  { name: "free_days", label: "CFS Free Days", type: "number", col: 3 },
  { name: "free_date", label: "CFS Free Date", type: "date", col: 3 },
  { name: "dem_upto", label: "Detention Free Days", type: "number", col: 3 },
  { name: "detention_upto", label: "Detention Free Date", type: "date", col: 3 },
  { name: "demurrage_free_days", label: "Demurrage Free Days", type: "number", col: 3 },
  { name: "demurrage_free_date", label: "Demurrage Free Date", type: "date", col: 3 },

  { type: "section", label: "Bill of Entry" },
  { name: "be_number", label: "BE Number", type: "text", col: 6 },
  { name: "be_date", label: "BE Date", type: "date", col: 6 },

  { type: "section", label: "Cargo Details" },
  { name: "cargo_desc", label: "Cargo Description", type: "text", col: 12 },
  { name: "hs_code", label: "HS Code", type: "text", col: 2 },
  { name: "origin", label: "Country of Origin", type: "text", col: 2 },
  {
    name: "container_type", label: "Container Type", type: "select", col: 2,
    options: [{ value: "FCL", label: "FCL" }, { value: "LCL", label: "LCL" }],
  },
  { name: "no_of_containers", label: "No. of Containers", type: "number", col: 2 },
  { name: "gross_weight", label: "Gross Weight", type: "number", col: 3 },
  { name: "net_weight", label: "Net Weight", type: "number", col: 2 },
  {
    name: "weight_unit", label: "Weight Unit", type: "select", col: 3,
    options: [{ value: "mt", label: "MT" }, { value: "kg", label: "KG" }],
  },
  { name: "packages", label: "No. of Packages", type: "number", col: 2 },
  { name: "package_unit", label: "Package Unit", type: "text", col: 2 },

  { type: "section", label: "Documents Received" },
  {
    name: "copy_doc", label: "Copy Document", type: "select", col: 3,
    options: [{ value: "YES", label: "YES" }, { value: "NO", label: "NO" }],
  },
  { name: "copy_doc_date", label: "Copy Doc Date", type: "date", col: 3 },
  {
    name: "orig_doc", label: "Original Document", type: "select", col: 3,
    options: [{ value: "YES", label: "YES" }, { value: "NO", label: "NO" }],
  },
  { name: "orig_doc_date", label: "Original Doc Date", type: "date", col: 3 },

  { type: "section", label: "License & Bond Linkage" },
  { name: "license_total_value", label: "License Total Value", type: "number", col: 2 },
  { name: "license_value", label: "License utilized value", type: "number", col: 2 },
  { name: "license_available_value", label: "License Available Value", type: "number", col: 2 },
  { name: "license_expiry_date", label: "License Expiry Date", type: "date", col: 3 },
  { name: "bond_total_value", label: "Bond Total Value", type: "number", col: 2 },
  { name: "bond_value", label: "Bond utilized Value", type: "number", col: 2 },
  { name: "bond_available_value", label: "Bond Available Value", type: "number", col: 2 },
  { name: "bond_expiry_date", label: "Bond Expiry Date", type: "date", col: 3 },

  { name: "remarks", label: "Remarks", type: "textarea", col: 12 },
];

// Archive view for jobs whose status is "Lot Completed" (8) — these are
// filtered out of the active All Jobs list and live here instead. There's
// no Edit action: once a job is closed out there's nothing left to change,
// but Documents/Containers/View stay available for historical reference.
const Job_history = () => {
  const [jobList, setJobsList] = useState([]);
  const [currentPage, setCurrentPage] = useState(0);

  const [docModal, setDocModal] = useState(false);
  const [docJob, setDocJob] = useState(null);
  const [containersModal, setContainersModal] = useState(false);
  const [containersJob, setContainersJob] = useState(null);
  const [viewModal, setViewModal] = useState(false);
  const [viewJob, setViewJob] = useState(null);

  const showAlert = useCallback((icon, title, text, timer = 2000) => {
    Swal.fire({ icon, title, text: text || "Something went wrong.", timer, showConfirmButton: false });
  }, []);

  const fetchData = useCallback(async () => {
    try {
      const response = await post("/all_jobs/job/list");
      if (response.status === 200) setJobsList(response.data.filter((j) => String(j.status) === "8"));
      else showAlert("error", response.status, response.message);
    } catch (err) {
      showAlert("error", "Unexpected Error", err.message);
    }
  }, [showAlert]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleDocs = useCallback((row) => {
    setDocJob(row);
    setDocModal(true);
  }, []);

  const handleContainers = useCallback((row) => {
    setContainersJob(row);
    setContainersModal(true);
  }, []);

  const handleView = useCallback((row) => {
    setViewJob(row);
    setViewModal(true);
  }, []);

  // Same field set every time (VIEW_SECTIONS is static) — only the values change.
  const viewFormData = useMemo(() => {
    if (!viewJob) return {};
    const data = { ...viewJob };
    data.currency_display = viewJob.currency_code
      ? `${viewJob.currency_code}${viewJob.currency_symbol ? ` (${viewJob.currency_symbol})` : ""}`
      : "";
    return data;
  }, [viewJob]);

  const columns = useMemo(() => {
    const staticColumns = [
      { header: "S.No", label_condition: "s_no", enableColumnFilter: false, enableSorting: false },
      {
        header: "Actions",
        cell: ({ row }) => (
          <div className="d-flex gap-2 align-items-center">
            <i
              className="ri-eye-fill text-info cursor-pointer"
              onClick={() => handleView(row)}
              title="View"
              style={{ fontSize: 18 }}
            />
            <i
              className="ri-folder-open-fill text-warning cursor-pointer"
              onClick={() => handleDocs(row)}
              title="Documents"
              style={{ fontSize: 18 }}
            />
            <i
              className="ri-building-4-fill text-success cursor-pointer"
              onClick={() => handleContainers(row)}
              title="Containers"
              style={{ fontSize: 18 }}
            />
          </div>
        ),
        enableSorting: false,
        enableColumnFilter: false,
      },
    ];

    const dynamicColumns =
      jobList?.length > 0
        ? Object.keys(jobList[0])
          .filter(
            (key) =>
              !["job_id", "doc_count", "packages", "currency_code", "currency_symbol", "shipper_name", "indenting_agent_name"].includes(key) &&
              !key.endsWith("_id") &&
              !key.endsWith("_id_fk")
          )
          .map((key) => ({
            header: key.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase()),
            accessorKey: key,
            ...(key === "status" && { label_condition: "job_status" }),
            ...(key === "invoice_value" && {
              cell: ({ row }) =>
                row.invoice_value ? `${row.currency_symbol || ""} ${row.invoice_value}`.trim() : row.invoice_value,
            }),
          }))
        : [];

    return [...staticColumns, ...dynamicColumns];
  }, [jobList, handleView, handleDocs, handleContainers]);

  return (
    <>
      <div className="page-header d-flex justify-content-between align-items-center">
        <div>
          <div className="page-header-title">Job History</div>
          <div className="page-header-sub">Lot Completed jobs, archived out of All Jobs</div>
        </div>
      </div>

      <div className="card basic-data-table mt-3">
        <div className="card-header">
          <h5 className="card-title mb-0">Job History</h5>
        </div>
        <div className="card-body">
          <Customtable
            table_title="Job History"
            columns={columns}
            data={jobList}
            isGlobalFilter={true}
            isPagination={true}
            currentPage={currentPage}
            setCurrentPage={setCurrentPage}
            isExportButton={true}
          />
        </div>
      </div>

      <JobFormModal
        show={viewModal}
        titleContent={
          <h5 className="modal-title mb-0">
            <i className="ri-eye-line me-2" />
            View Job {viewJob?.job_no ? `— ${viewJob.job_no}` : ""}
          </h5>
        }
        formFields={VIEW_SECTIONS.map((f) => (f.type === "section" ? f : { ...f, readOnly: true }))}
        formData={viewFormData}
        onChange={() => {}}
        onClose={() => { setViewModal(false); setViewJob(null); }}
      />

      <JobDocumentModal
        show={docModal}
        job={docJob}
        onClose={() => { setDocModal(false); setDocJob(null); }}
      />
      <JobContainersModal
        show={containersModal}
        job={containersJob}
        onClose={() => { setContainersModal(false); setContainersJob(null); }}
      />
    </>
  );
};

export default Job_history;
